/**
 * tenant#138 — sale-window display helpers for the catalog billing
 * document. The payload's OWN timezone and the SQL bitmask (bit0=Monday
 * … bit6=Sunday) are the only clocks this module knows; it never reads
 * the browser zone. No money is computed here.
 */

export interface WeekdaySpan {
  readonly start: number;
  readonly end: number;
}

const WEEKDAY_COUNT = 7;

/**
 * Expand a `days_of_week` bitmask into 0-based weekday indexes
 * (0=Monday … 6=Sunday), matching `EXTRACT(ISODOW FROM …)::int - 1`
 * on the rating side.
 */
export function weekdayIndexesFromMask(mask: number): readonly number[] {
  if (!Number.isFinite(mask) || mask <= 0) {
    return [];
  }
  const days: number[] = [];
  const bits = Math.trunc(mask);
  for (let bit = 0; bit < WEEKDAY_COUNT; bit += 1) {
    if ((bits & (1 << bit)) !== 0) {
      days.push(bit);
    }
  }
  return days;
}

/**
 * Compress a sorted 0..6 index list into inclusive weekday spans.
 * Monday–Friday is one span; Saturday–Sunday is one span; a hole
 * (Mon, Wed, Fri) stays three one-day spans.
 */
export function weekdaySpans(days: readonly number[]): readonly WeekdaySpan[] {
  const first = days[0];
  if (first === undefined) {
    return [];
  }
  const spans: WeekdaySpan[] = [];
  let start = first;
  let prev = first;
  for (let i = 1; i < days.length; i += 1) {
    const day = days[i];
    if (day === undefined) {
      break;
    }
    if (day === prev + 1) {
      prev = day;
      continue;
    }
    spans.push({ start, end: prev });
    start = day;
    prev = day;
  }
  spans.push({ start, end: prev });
  return spans;
}

/**
 * Minutes from local midnight as `HH:MM`. 1440 renders as `24:00` so a
 * half-open `[1080, 1440)` window reads 18:00–24:00 rather than wrapping
 * to 00:00. Negative / non-finite inputs yield an empty string — the
 * caller drops the window rather than guessing a clock.
 */
export function formatMinuteOfDay(minutes: number): string {
  if (!Number.isFinite(minutes)) {
    return '';
  }
  const total = Math.trunc(minutes);
  if (total < 0) {
    return '';
  }
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * `start_minute >= end_minute` is a midnight-crossing window that
 * belongs to the START day (the SQL match). Callers still print
 * `start–end`; this flag is the documented rule, not extra arithmetic.
 */
export function saleWindowCrossesMidnight(startMinute: number, endMinute: number): boolean {
  return startMinute >= endMinute;
}
